import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addRecruitmentCandidate,
  clearRecruitmentError,
  fetchAdminCandidates,
  fetchShortlistedApplicationsByJob,
  reassignCandidateAssessors,
  setCandidateArchived,
  updateAdminReview,
} from "../../../store/slices/recruitmentSlice";
import {
  sendRecruitmentAssessorReassignedNotification,
  sendRecruitmentCandidateAssignedNotification,
  sendRecruitmentFinalReviewNotification,
} from "../../../emailjs/emailService";
import {
  fetchAllJobPosts,
  fetchApplicationBySource,
} from "../../../services/jobApplicationsService";
import "./Recruitment.scss";

const defaultCandidateForm = {
  name: "",
  email: "",
  positionTitle: "",
  assessorOne: "",
  assessorTwo: "",
  gitHubProject: "",
  adminInitialRemarks: "",
  sourceJobId: "",
  sourceJobTitle: "",
  sourceApplicationId: "",
};

const AdminRecruitment = () => {
  const dispatch = useDispatch();
  const {
    adminCandidates,
    loadingAdmin,
    mutating,
    error,
    shortlistedApplications,
  } =
    useSelector((state) => state.recruitment);
  const { employees, user } = useSelector((state) => state.auth);

  const [statusFilter, setStatusFilter] = useState("live");
  const [showAddForm, setShowAddForm] = useState(false);
  const [candidateForm, setCandidateForm] = useState(defaultCandidateForm);
  const [cvFile, setCvFile] = useState(null);
  const [reviewingCandidate, setReviewingCandidate] = useState(null);
  const [reviewApplicationDetails, setReviewApplicationDetails] = useState(null);
  const [reviewApplicationLoading, setReviewApplicationLoading] = useState(false);
  const [jobPosts, setJobPosts] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [reviewForm, setReviewForm] = useState({
    finalReview: "Undecided",
    finalRemarks: "",
    adminChecked: false,
    assessorOne: "",
    assessorTwo: "",
  });
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    dispatch(fetchAdminCandidates(statusFilter));
    return () => dispatch(clearRecruitmentError());
  }, [dispatch, statusFilter]);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const rows = await fetchAllJobPosts();
        setJobPosts(rows);
      } catch (err) {
        console.error("Failed to load job posts for recruitment picker:", err);
      }
    };
    loadJobs();
  }, []);

  useEffect(() => {
    if (candidateForm.sourceJobId) {
      dispatch(fetchShortlistedApplicationsByJob(candidateForm.sourceJobId));
    }
  }, [candidateForm.sourceJobId, dispatch]);

  useEffect(() => {
    if (!reviewingCandidate) return;
    setReviewForm({
      finalReview: reviewingCandidate.finalReview || "Undecided",
      finalRemarks: reviewingCandidate.finalRemarks || "",
      adminChecked: Boolean(reviewingCandidate.adminChecked),
      assessorOne: reviewingCandidate.assessorOne || "",
      assessorTwo: reviewingCandidate.assessorTwo || "",
    });
  }, [reviewingCandidate]);

  useEffect(() => {
    const loadReviewApplication = async () => {
      if (!reviewingCandidate?.sourceJobId || !reviewingCandidate?.sourceApplicationId) {
        setReviewApplicationDetails(null);
        return;
      }
      try {
        setReviewApplicationLoading(true);
        const details = await fetchApplicationBySource(
          reviewingCandidate.sourceJobId,
          reviewingCandidate.sourceApplicationId
        );
        setReviewApplicationDetails(details);
      } catch (err) {
        console.error("Failed to load review application details:", err);
        setReviewApplicationDetails(null);
      } finally {
        setReviewApplicationLoading(false);
      }
    };
    loadReviewApplication();
  }, [reviewingCandidate?.sourceApplicationId, reviewingCandidate?.sourceJobId]);

  const assessorOptions = useMemo(
    () => employees.filter((emp) => emp?.email).map((emp) => emp.email),
    [employees]
  );

  const refreshCurrentList = () => dispatch(fetchAdminCandidates(statusFilter));

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    setLocalError("");
    if (!candidateForm.assessorOne || !candidateForm.assessorTwo) {
      setLocalError("Please assign both assessor one and assessor two.");
      return;
    }
    if (candidateForm.assessorOne === candidateForm.assessorTwo) {
      setLocalError("Assessor one and assessor two must be different.");
      return;
    }
    try {
      const action = await dispatch(
        addRecruitmentCandidate({ candidateData: candidateForm, cvFile })
      ).unwrap();
      await sendRecruitmentCandidateAssignedNotification(action);
      setCandidateForm(defaultCandidateForm);
      setSelectedApplicationId("");
      setCvFile(null);
      setShowAddForm(false);
      refreshCurrentList();
    } catch (err) {
      console.error("Failed to add recruitment candidate:", err);
    }
  };

  const handleSelectJob = (jobId) => {
    const job = jobPosts.find((item) => item.id === jobId);
    setSelectedApplicationId("");
    setCandidateForm((prev) => ({
      ...prev,
      sourceJobId: jobId || "",
      sourceJobTitle: job?.title || "",
      sourceApplicationId: "",
      positionTitle: job?.title || prev.positionTitle,
    }));
  };

  const handleSelectApplication = (applicationId) => {
    setSelectedApplicationId(applicationId);
    const app = shortlistedApplications.find((item) => item.id === applicationId);
    if (!app) return;
    setCandidateForm((prev) => ({
      ...prev,
      sourceApplicationId: app.id,
      name:
        `${app.firstName || ""} ${app.lastName || ""}`.trim() || prev.name,
      email: app.email || prev.email,
      gitHubProject: app.githubUrl || prev.gitHubProject,
      candidateCV: app.resumeUrl || prev.candidateCV,
      adminInitialRemarks: prev.adminInitialRemarks,
    }));
  };

  const handleArchiveToggle = async (candidate, archived) => {
    try {
      await dispatch(
        setCandidateArchived({ candidateId: candidate.id, archived })
      ).unwrap();
      refreshCurrentList();
    } catch (err) {
      console.error("Failed to update candidate status:", err);
    }
  };

  const handleSaveFinalReview = async () => {
    if (!reviewingCandidate) return;
    try {
      await dispatch(
        updateAdminReview({
          candidateId: reviewingCandidate.id,
          finalReview: reviewForm.finalReview,
          finalRemarks: reviewForm.finalRemarks,
          adminChecked: reviewForm.adminChecked,
        })
      ).unwrap();
      await sendRecruitmentFinalReviewNotification(
        reviewingCandidate,
        reviewForm.finalReview,
        reviewForm.finalRemarks
      );
      setReviewingCandidate(null);
      refreshCurrentList();
    } catch (err) {
      console.error("Failed to save final review:", err);
    }
  };

  const handleReassignAssessors = async () => {
    if (!reviewingCandidate) return;
    setLocalError("");
    if (!reviewForm.assessorOne || !reviewForm.assessorTwo) {
      setLocalError("Both assessor slots are required.");
      return;
    }
    if (reviewForm.assessorOne === reviewForm.assessorTwo) {
      setLocalError("Assessor one and assessor two must be different.");
      return;
    }
    const previousAssessors = [
      reviewingCandidate.assessorOne,
      reviewingCandidate.assessorTwo,
    ].filter(Boolean);
    const newAssessors = [reviewForm.assessorOne, reviewForm.assessorTwo].filter(
      Boolean
    );
    try {
      await dispatch(
        reassignCandidateAssessors({
          candidateId: reviewingCandidate.id,
          assessorOne: reviewForm.assessorOne,
          assessorTwo: reviewForm.assessorTwo,
        })
      ).unwrap();
      await sendRecruitmentAssessorReassignedNotification(
        reviewingCandidate,
        previousAssessors,
        newAssessors
      );
      setReviewingCandidate((prev) =>
        prev
          ? {
              ...prev,
              assessorOne: reviewForm.assessorOne,
              assessorTwo: reviewForm.assessorTwo,
            }
          : prev
      );
      refreshCurrentList();
    } catch (err) {
      console.error("Failed to reassign assessors:", err);
    }
  };

  return (
    <div className="recruitment-page">
      <div className="page-header">
        <h1>Recruitment Management</h1>
        <p>Manage candidate assessments, reassignment, and final review.</p>
      </div>

      <div className="recruitment-actions">
        <div className="status-filters">
          <button
            className={statusFilter === "live" ? "active" : ""}
            onClick={() => setStatusFilter("live")}
            type="button"
          >
            Active
          </button>
          <button
            className={statusFilter === "archived" ? "active" : ""}
            onClick={() => setStatusFilter("archived")}
            type="button"
          >
            Archived
          </button>
        </div>
        <button type="button" onClick={() => setShowAddForm((s) => !s)}>
          {showAddForm ? "Hide Add Candidate" : "Add New Candidate"}
        </button>
      </div>

      {showAddForm && (
        <form className="recruitment-form-card" onSubmit={handleAddCandidate}>
          <h3 className="section-title">Candidate Intake</h3>
          <div className="grid">
            <input
              className="form-input"
              placeholder="Candidate name"
              value={candidateForm.name}
              onChange={(e) =>
                setCandidateForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
            <input
              className="form-input"
              placeholder="Candidate email"
              type="email"
              value={candidateForm.email}
              onChange={(e) =>
                setCandidateForm((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
            <input
              className="form-input"
              placeholder="Position title"
              value={candidateForm.positionTitle}
              onChange={(e) =>
                setCandidateForm((prev) => ({
                  ...prev,
                  positionTitle: e.target.value,
                }))
              }
              required
            />
            <select
              className="form-input"
              value={candidateForm.sourceJobId}
              onChange={(e) => handleSelectJob(e.target.value)}
            >
              <option value="">Select job post (for shortlisted applicants)</option>
              {jobPosts.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title || job.id}
                </option>
              ))}
            </select>
            <select
              className="form-input"
              value={selectedApplicationId}
              onChange={(e) => handleSelectApplication(e.target.value)}
              disabled={!candidateForm.sourceJobId}
            >
              <option value="">
                {candidateForm.sourceJobId
                  ? "Select shortlisted application"
                  : "Select job first"}
              </option>
              {shortlistedApplications.map((app) => (
                <option key={app.id} value={app.id}>
                  {`${app.firstName || ""} ${app.lastName || ""}`.trim()} ({app.email || "no-email"})
                </option>
              ))}
            </select>
            <input
              className="form-input"
              placeholder="GitHub project URL"
              value={candidateForm.gitHubProject}
              onChange={(e) =>
                setCandidateForm((prev) => ({
                  ...prev,
                  gitHubProject: e.target.value,
                }))
              }
            />
            <select
              className="form-input"
              value={candidateForm.assessorOne}
              onChange={(e) =>
                setCandidateForm((prev) => ({
                  ...prev,
                  assessorOne: e.target.value,
                }))
              }
              required
            >
              <option value="">Select assessor one</option>
              {assessorOptions.map((email) => (
                <option key={`ass1-${email}`} value={email}>
                  {email}
                </option>
              ))}
            </select>
            <select
              className="form-input"
              value={candidateForm.assessorTwo}
              onChange={(e) =>
                setCandidateForm((prev) => ({
                  ...prev,
                  assessorTwo: e.target.value,
                }))
              }
              required
            >
              <option value="">Select assessor two</option>
              {assessorOptions.map((email) => (
                <option key={`ass2-${email}`} value={email}>
                  {email}
                </option>
              ))}
            </select>
            <input
              className="form-input"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setCvFile(e.target.files?.[0] || null)}
            />
          </div>
          {selectedApplicationId && (
            <div className="application-source-chip">
              Using shortlisted application: <strong>{selectedApplicationId}</strong>
            </div>
          )}
          <textarea
            className="form-input"
            placeholder="Initial admin remarks"
            value={candidateForm.adminInitialRemarks}
            onChange={(e) =>
              setCandidateForm((prev) => ({
                ...prev,
                adminInitialRemarks: e.target.value,
              }))
            }
            rows={3}
          />
          <button type="submit" disabled={mutating}>
            {mutating ? "Saving..." : "Create Candidate"}
          </button>
        </form>
      )}

      {(error || localError) && <div className="error-box">{error || localError}</div>}

      <div className="table-wrap card-surface">
        <h3 className="section-title">Recruitment Candidates</h3>
        <table className="recruitment-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Assessor 1</th>
              <th>Assessor 2</th>
              <th>Scores</th>
              <th>Final</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingAdmin ? (
              <tr>
                <td colSpan={8}>Loading candidates...</td>
              </tr>
            ) : adminCandidates.length === 0 ? (
              <tr>
                <td colSpan={8}>No candidates found for this status.</td>
              </tr>
            ) : (
              adminCandidates.map((candidate) => (
                <tr key={candidate.id}>
                  <td>{candidate.name}</td>
                  <td>{candidate.email}</td>
                  <td>{candidate.positionTitle}</td>
                  <td>{candidate.assessorOne || "-"}</td>
                  <td>{candidate.assessorTwo || "-"}</td>
                  <td>
                    {(candidate.oneScore || 0) + (candidate.twoScore || 0)} (
                    {candidate.oneScore || 0}/{candidate.twoScore || 0})
                  </td>
                  <td>{candidate.finalReview || "Pending"}</td>
                  <td className="actions">
                    <button type="button" onClick={() => setReviewingCandidate(candidate)}>
                      Review
                    </button>
                    {candidate.candidateStatus === "live" ? (
                      <button
                        type="button"
                        onClick={() => handleArchiveToggle(candidate, true)}
                      >
                        Archive
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleArchiveToggle(candidate, false)}
                      >
                        Unarchive
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {reviewingCandidate && (
        <div className="recruitment-modal-backdrop" onClick={() => setReviewingCandidate(null)}>
          <div className="recruitment-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Admin Review - {reviewingCandidate.name}</h3>
            <div className="modal-grid">
              <p>
                <strong>Position:</strong> {reviewingCandidate.positionTitle}
              </p>
              <p>
                <strong>Candidate CV:</strong>{" "}
                {reviewingCandidate.candidateCV ? (
                  <a href={reviewingCandidate.candidateCV} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  "N/A"
                )}
              </p>
              <p>
                <strong>GitHub:</strong>{" "}
                {reviewingCandidate.gitHubProject ? (
                  <a href={reviewingCandidate.gitHubProject} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  "N/A"
                )}
              </p>
              <p>
                <strong>Assessor 1 Review:</strong> {reviewingCandidate.assOneReview || "Pending"}
              </p>
              <p>
                <strong>Assessor 2 Review:</strong> {reviewingCandidate.assTwoReview || "Pending"}
              </p>
            </div>

            <div className="review-controls">
              <label>Application Profile (from Careers)</label>
              {reviewApplicationLoading ? (
                <p>Loading linked application details...</p>
              ) : reviewApplicationDetails ? (
                <div className="modal-grid">
                  <p><strong>Job:</strong> {reviewApplicationDetails.sourceJobTitle || "N/A"}</p>
                  <p><strong>Location:</strong> {reviewApplicationDetails.location || "N/A"}</p>
                  <p><strong>Current Position:</strong> {reviewApplicationDetails.currentPosition || "N/A"}</p>
                  <p><strong>Current Company:</strong> {reviewApplicationDetails.currentCompany || "N/A"}</p>
                  <p><strong>Experience:</strong> {reviewApplicationDetails.yearsOfExperience || "N/A"}</p>
                  <p><strong>Expected Salary:</strong> {reviewApplicationDetails.expectedSalary || "N/A"}</p>
                  <p>
                    <strong>LinkedIn:</strong>{" "}
                    {reviewApplicationDetails.linkedInUrl ? (
                      <a href={reviewApplicationDetails.linkedInUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Portfolio:</strong>{" "}
                    {reviewApplicationDetails.portfolioUrl ? (
                      <a href={reviewApplicationDetails.portfolioUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>GitHub (application):</strong>{" "}
                    {reviewApplicationDetails.githubUrl ? (
                      <a href={reviewApplicationDetails.githubUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Resume:</strong>{" "}
                    {reviewApplicationDetails.resumeUrl ? (
                      <a href={reviewApplicationDetails.resumeUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Cover Letter File:</strong>{" "}
                    {reviewApplicationDetails.coverLetterUrl ? (
                      <a href={reviewApplicationDetails.coverLetterUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                </div>
              ) : (
                <p>No linked careers application data found for this candidate.</p>
              )}
              {!reviewApplicationLoading && reviewApplicationDetails && (
                <div className="longtext-grid">
                  <div className="longtext-card">
                    <p className="longtext-title">Cover Letter Text</p>
                    <p className="longtext-content">
                      {reviewApplicationDetails.coverLetter || "N/A"}
                    </p>
                  </div>
                  <div className="longtext-card">
                    <p className="longtext-title">Additional Info</p>
                    <p className="longtext-content">
                      {reviewApplicationDetails.additionalInfo || "N/A"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="review-controls">
              <label>Final Decision</label>
              <select
                className="form-input"
                value={reviewForm.finalReview}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, finalReview: e.target.value }))
                }
              >
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Undecided">Undecided</option>
              </select>

              <label>
                <input
                  type="checkbox"
                  checked={reviewForm.adminChecked}
                  onChange={(e) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      adminChecked: e.target.checked,
                    }))
                  }
                />
                Close assessor editing (admin final lock)
              </label>

              <textarea
                className="form-input"
                rows={3}
                value={reviewForm.finalRemarks}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, finalRemarks: e.target.value }))
                }
                placeholder="Final remarks"
              />
            </div>

            <div className="review-controls">
              <label>Reassign Assessor One</label>
              <select
                className="form-input"
                value={reviewForm.assessorOne}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, assessorOne: e.target.value }))
                }
              >
                <option value="">Select assessor one</option>
                {assessorOptions.map((email) => (
                  <option key={`m1-${email}`} value={email}>
                    {email}
                  </option>
                ))}
              </select>

              <label>Reassign Assessor Two</label>
              <select
                className="form-input"
                value={reviewForm.assessorTwo}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, assessorTwo: e.target.value }))
                }
              >
                <option value="">Select assessor two</option>
                {assessorOptions.map((email) => (
                  <option key={`m2-${email}`} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button type="button" onClick={handleReassignAssessors} disabled={mutating}>
                Save Assessor Reassignment
              </button>
              <button type="button" onClick={handleSaveFinalReview} disabled={mutating}>
                Save Final Review
              </button>
              <button type="button" onClick={() => setReviewingCandidate(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecruitment;
